import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  useContentToken,
  useEditMode,
  usePage,
  useProperty,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage, cn } from '@vibesites/ui-primitives';
import { ChevronDown, Menu, Phone, X } from 'lucide-react';
import { ThemeStyles } from '../theme/ThemeStyles';
import { useSiteMotion } from '../theme/useSiteMotion';
import {
  buildNavLinks,
  formatPhone,
  hasValue,
  isActivePath,
  linkTarget,
  stripTags,
  telHref,
} from '../utils/content';

const logoDef: SlotGroupDefinition = {
  label: 'Logo',
  description: 'Shown on the evergreen header bar. A white or light logo reads best.',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'],
    },
  },
};

const offerDef: SlotGroupDefinition = {
  label: 'Special Offer Bar',
  description: 'Rust bar above the header. Hidden when the title and details are empty.',
  fields: {
    title: { label: 'Title', type: 'text', defaultField: 'special_offers[0].title' },
    description: { label: 'Details', type: 'richtext', defaultField: 'special_offers[0].description' },
  },
  sourceBinding: {
    collection: 'special_offers',
    defaultIndex: 0,
    fieldMappings: { title: 'title', description: 'description' },
  },
};

const residentLinkDef: SlotGroupDefinition = {
  label: 'Resident Login',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_resident_portal_url' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'resident_portal_clicked' },
  },
};

const applicantLinkDef: SlotGroupDefinition = {
  label: 'Applicant Login',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      defaultValue: '/floor-plans',
      suggestedFields: ['property_apply_now_url', 'property_online_leasing_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Applicant' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'apply_now_clicked' },
  },
};

function Wordmark({ name }: { name: string }) {
  return (
    <span className="block max-w-[240px] font-heading text-[20px] font-semibold uppercase leading-tight tracking-[0.14em] text-[color:var(--vibe-color-text-on-secondary)] sm:text-[22px] xl:max-w-[340px] xl:text-center xl:text-[21px]">
      {name || 'Home'}
    </span>
  );
}

export function Header() {
  const page = usePage();
  const editMode = useEditMode();
  const property = useProperty() as unknown as Record<string, unknown>;
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loginRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLSpanElement>(null);

  useSiteMotion();

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const y = window.scrollY;
      // Hysteresis: compacting the bar shortens the page, so one threshold would flicker.
      setScrolled((prev) => (prev ? y > 8 : y > 72));
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`;
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const logo = useSlotGroup('header.logo', logoDef);
  const offer = useSlotGroup('header.offer', offerDef);
  const resident = useSlotGroup('header.resident_link', residentLinkDef);
  const applicant = useSlotGroup('header.applicant_link', applicantLinkDef);
  const loginLabel = useContentToken('header.login_label');
  const phone = useContentToken('header.phone');

  const phoneValue = hasValue(phone) ? phone : String(property?.property_phone ?? '');
  const propertyName = String(property?.property_name ?? '');

  // Colored logos rarely read on the evergreen bar, so without a light logo the name renders as a wordmark.
  const logoUrl = hasValue(logo.image) ? logo.image : '';

  const navLinks = useMemo(() => buildNavLinks().filter((l) => l.alias !== 'home'), []);
  const half = Math.ceil(navLinks.length / 2);
  const leftLinks = navLinks.slice(0, half);
  const rightLinks = navLinks.slice(half);

  const hasResident = hasValue(resident.url) || !!editMode?.isEditMode;
  const hasOffer = hasValue(offer.title) || hasValue(offer.description);
  const offerHasDetails = hasValue(offer.description) && hasValue(offer.title);

  useEffect(() => {
    if (!loginOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (loginRef.current && !loginRef.current.contains(e.target as Node)) setLoginOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setLoginOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [loginOpen]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    if (!menuOpen) return () => { document.body.style.overflow = ''; };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const navLinkClass = (href: string) =>
    cn(
      'relative whitespace-nowrap font-body text-[16px] font-semibold leading-tight text-[color:var(--vibe-color-text-on-secondary)] no-underline',
      'after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-current after:transition-transform after:duration-[250ms]',
      'hover:after:scale-x-100 focus-visible:after:scale-x-100',
      isActivePath(page?.path, href) && 'after:scale-x-100',
    );

  const logoNode = (
    <EditableSlotGroup groupPath="header.logo" definition={logoDef}>
      <a href="/" className="block" aria-label={`${propertyName || 'Home'} home`}>
        {logoUrl ? (
          <VibesitesImage
            url={logoUrl}
            alt={propertyName}
            sizes="200px"
            priority
            className="h-9 w-auto max-w-[170px] object-contain sm:h-10 xl:h-11 xl:max-w-[200px]"
          />
        ) : (
          <Wordmark name={propertyName} />
        )}
      </a>
    </EditableSlotGroup>
  );

  const loginMenu = (
    <div ref={loginRef} className="relative">
      <button
        type="button"
        onClick={() => setLoginOpen((v) => !v)}
        aria-expanded={loginOpen}
        aria-haspopup="true"
        className="inline-flex h-10 items-center gap-1.5 bg-primary px-4 font-body text-[15px] font-bold uppercase tracking-wide text-[color:var(--vibe-color-text-on-primary)] js-btn hover:brightness-110 sm:h-11 sm:px-4"
      >
        <EditableToken path="header.login_label" as="span">
          {loginLabel || 'Login'}
        </EditableToken>
        <ChevronDown className={cn('h-4 w-4 transition-transform duration-[250ms]', loginOpen && 'rotate-180')} aria-hidden />
      </button>
      <div
        className={cn(
          'absolute right-0 top-full z-10 mt-1 min-w-[170px] origin-top-right bg-background py-1 text-foreground shadow-lg transition-[opacity,transform,visibility] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
          loginOpen ? 'visible translate-y-0 scale-100 opacity-100' : 'invisible -translate-y-2 scale-95 opacity-0',
        )}
      >
        <EditableSlotGroup groupPath="header.resident_link" definition={residentLinkDef} className={cn(!hasResident && 'hidden')}>
          <a
            href={resident.url || '#'}
            target={linkTarget(resident.url, resident._target)}
            rel="noopener noreferrer"
            data-vibesite-event={resident.vibesite_event || undefined}
            className="block px-4 py-2.5 font-body text-[15px] font-semibold text-foreground no-underline transition-[background-color,color,padding] duration-200 hover:bg-muted hover:pl-5 hover:text-primary"
          >
            {resident.label || 'Resident'}
          </a>
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="header.applicant_link" definition={applicantLinkDef}>
          <a
            href={applicant.url || '#'}
            target={linkTarget(applicant.url, applicant._target)}
            rel="noopener noreferrer"
            data-vibesite-event={applicant.vibesite_event || undefined}
            className="block px-4 py-2.5 font-body text-[15px] font-semibold text-foreground no-underline transition-[background-color,color,padding] duration-200 hover:bg-muted hover:pl-5 hover:text-primary"
          >
            {applicant.label || 'Applicant'}
          </a>
        </EditableSlotGroup>
      </div>
    </div>
  );

  return (
    <>
      <ThemeStyles />
      <header className="js-site-header sticky top-0 z-50 w-full">
        <EditableSlotGroup groupPath="header.offer" definition={offerDef}>
          {hasOffer ? (
            <div className="bg-primary text-[color:var(--vibe-color-text-on-primary)]">
              <button
                type="button"
                onClick={() => offerHasDetails && setOfferOpen((v) => !v)}
                aria-expanded={offerHasDetails ? offerOpen : undefined}
                className="mx-auto flex w-full max-w-screen-xl items-center justify-center gap-2 px-4 py-2.5 text-center font-body text-[15px] font-semibold leading-snug sm:text-[17px]"
              >
                {offerHasDetails && (
                  <ChevronDown className={cn('h-5 w-5 shrink-0 transition-transform duration-[250ms]', offerOpen && 'rotate-180')} aria-hidden />
                )}
                <span>{hasValue(offer.title) ? offer.title : stripTags(offer.description || '')}</span>
              </button>
              {offerHasDetails && (
                <div
                  className={cn(
                    'grid overflow-hidden transition-[grid-template-rows] duration-[250ms]',
                    offerOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                  )}
                >
                  <div className="min-h-0">
                    <div
                      className="js-copy mx-auto max-w-3xl px-6 pb-4 text-center !text-[15px] !leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: offer.description || '' }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <span className="block h-0" />
          )}
        </EditableSlotGroup>

        <div
          className={cn(
            'relative bg-secondary text-[color:var(--vibe-color-text-on-secondary)] transition-shadow duration-500',
            scrolled ? 'shadow-[0_10px_30px_-12px_rgba(0,0,0,0.55)]' : 'shadow-md',
          )}
        >
          {/* Desktop: split nav around a centered logo */}
          <div
            className={cn(
              'mx-auto hidden max-w-[1440px] grid-cols-[1fr_auto_1fr] items-center gap-8 px-8 transition-[padding] duration-500 ease-out xl:grid',
              scrolled ? 'py-2' : 'py-4',
            )}
          >
            <nav aria-label="Primary" className="flex flex-wrap items-center justify-end gap-x-6 gap-y-3">
              {leftLinks.map((l) => (
                <a key={l.href} href={l.href} className={navLinkClass(l.href)}>
                  {l.label}
                </a>
              ))}
            </nav>
            <div className={cn('px-2 transition-transform duration-500 ease-out', scrolled && 'scale-[0.86]')}>{logoNode}</div>
            <div className="flex items-center justify-between gap-6">
              <nav aria-label="Secondary" className="flex flex-wrap items-center gap-x-6 gap-y-3">
                {rightLinks.map((l) => (
                  <a key={l.href} href={l.href} className={navLinkClass(l.href)}>
                    {l.label}
                  </a>
                ))}
              </nav>
              {loginMenu}
            </div>
          </div>

          {/* Tablet / mobile bar */}
          <div
            className={cn(
              'flex items-center justify-between gap-4 px-4 transition-[padding] duration-500 ease-out sm:px-6 xl:hidden',
              scrolled ? 'py-2' : 'py-3',
            )}
          >
            <div className={cn('origin-left transition-transform duration-500 ease-out', scrolled && 'scale-[0.9]')}>{logoNode}</div>
            <div className="flex items-center gap-2 sm:gap-3">
              {hasValue(phoneValue) && (
                <a
                  href={telHref(phoneValue)}
                  className="hidden h-11 items-center gap-2 px-2 font-body text-[15px] font-semibold text-[color:var(--vibe-color-text-on-secondary)] no-underline md:inline-flex"
                >
                  <Phone className="h-4 w-4" aria-hidden />
                  {formatPhone(phoneValue)}
                </a>
              )}
              <div className="hidden sm:block">{loginMenu}</div>
              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                aria-label="Open menu"
                aria-expanded={menuOpen}
                aria-controls="js-mobile-menu"
                className="group inline-flex h-11 w-11 items-center justify-center text-[color:var(--vibe-color-text-on-secondary)] transition-transform duration-300 active:scale-90"
              >
                <Menu className="h-7 w-7 transition-transform duration-300 group-hover:scale-110" aria-hidden />
              </button>
            </div>
          </div>
          <span
            ref={progressRef}
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] origin-left bg-primary"
            style={{ transform: 'scaleX(0)' }}
          />
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        aria-hidden="true"
        onClick={() => setMenuOpen(false)}
        className={cn(
          'fixed inset-0 z-[60] bg-black/55 transition-opacity duration-[250ms] xl:hidden',
          menuOpen ? 'visible opacity-100' : 'invisible pointer-events-none opacity-0',
        )}
      />
      <div
        id="js-mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        className={cn(
          'fixed inset-y-0 right-0 z-[61] flex w-[86%] max-w-[400px] flex-col bg-secondary text-[color:var(--vibe-color-text-on-secondary)] shadow-xl transition-[transform,visibility] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] xl:hidden',
          menuOpen ? 'visible translate-x-0' : 'invisible translate-x-full',
        )}
      >
        <div className="flex items-center justify-between px-4 py-3 sm:px-6">
          <div>
            {logoUrl ? (
              <VibesitesImage
                url={logoUrl}
                alt={propertyName}
                sizes="170px"
                className="h-9 w-auto max-w-[170px] object-contain sm:h-10"
              />
            ) : (
              <Wordmark name={propertyName} />
            )}
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
            className="group inline-flex h-11 w-11 items-center justify-center text-[color:var(--vibe-color-text-on-secondary)]"
          >
            <X className="h-7 w-7 transition-transform duration-300 group-hover:rotate-90" aria-hidden />
          </button>
        </div>
        <nav aria-label="Mobile" className="flex-1 overflow-y-auto px-6 pb-8 pt-4">
          <ul className="flex flex-col">
            {[{ alias: 'home', label: 'Home', href: '/' }, ...navLinks].map((l, i) => (
              <li
                key={l.href}
                className={cn(
                  'transition-[opacity,transform] duration-500 ease-out',
                  menuOpen ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0',
                )}
                style={{ transitionDelay: menuOpen ? `${120 + i * 45}ms` : '0ms' }}
              >
                <a
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className={cn(
                    'block py-3 font-body text-[19px] font-medium text-[color:var(--vibe-color-text-on-secondary)] no-underline transition-[color,transform] duration-300 hover:translate-x-1.5 hover:text-primary',
                    isActivePath(page?.path, l.href) && 'text-primary',
                  )}
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
          <div
            className={cn(
              'mt-8 flex flex-col gap-3 transition-[opacity,transform] duration-500 ease-out',
              menuOpen ? 'translate-y-0 opacity-100 delay-500' : 'translate-y-4 opacity-0',
            )}
          >
            {hasResident && <a
              href={resident.url || '#'}
              target={linkTarget(resident.url, resident._target)}
              rel="noopener noreferrer"
              data-vibesite-event={resident.vibesite_event || undefined}
              className="inline-flex min-h-[48px] items-center justify-center border border-[color:var(--vibe-color-text-on-secondary)] px-4 font-heading text-[17px] font-bold uppercase text-[color:var(--vibe-color-text-on-secondary)] no-underline js-btn hover:bg-[color:var(--vibe-color-text-on-secondary)] hover:text-secondary"
            >
              {resident.label || 'Resident'}
            </a>}
            <a
              href={applicant.url || '#'}
              target={linkTarget(applicant.url, applicant._target)}
              rel="noopener noreferrer"
              data-vibesite-event={applicant.vibesite_event || undefined}
              className="inline-flex min-h-[48px] items-center justify-center bg-primary px-4 font-heading text-[17px] font-bold uppercase text-[color:var(--vibe-color-text-on-primary)] no-underline js-btn"
            >
              {applicant.label || 'Applicant'}
            </a>
          </div>
          {hasValue(phoneValue) && (
            <a
              href={telHref(phoneValue)}
              className="mt-6 inline-flex items-center gap-2 font-body text-lg font-semibold text-[color:var(--vibe-color-text-on-secondary)] no-underline"
            >
              <Phone className="h-5 w-5" aria-hidden />
              <EditableToken path="header.phone" as="span">
                {formatPhone(phoneValue)}
              </EditableToken>
            </a>
          )}
        </nav>
      </div>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.login_label': { label: 'Login Button Text', type: 'text', defaultValue: 'Login' },
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
  },
  slotGroups: {
    'header.logo': logoDef,
    'header.offer': offerDef,
    'header.resident_link': residentLinkDef,
    'header.applicant_link': applicantLinkDef,
  },
};

(Header as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Header;
